import { Component, computed, Inject, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { InputAttrTypeEnum } from '@vueling-ng/vy-ds/src/lib/components/globals/forms/input';
import {
  SIDEPANEL_DATA,
  SidePanelRef,
  SidePanelService
} from '@vueling-ng/vy-ds/src/lib/components/globals/side-panel';
import { BehaviorSubject, Subject } from 'rxjs';
import { Filters } from '../../models/bookings.interface';
import { emptyFilters, FilterTextBox, QuickFiltersValue } from '../../models/filters.model';
import { BookingListFilterService } from '../../services/booking-list-filter.service';

@Component({
  selector: 'app-filter-panel',
  templateUrl: './filter-panel.component.html'
})
export class FilterPanelComponent implements OnInit, OnDestroy {
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _sidePanelService: SidePanelService = inject(SidePanelService);
  private readonly _bookingListFilterService: BookingListFilterService = inject(BookingListFilterService);

  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  public readonly InputAttrTypeEnum = InputAttrTypeEnum;
  public readonly filterValues = new BehaviorSubject<FilterTextBox[]>(this.data.availableFilters);

  public isFilterActive = computed(() => {
    const currentFilters = this._bookingListFilterService.getCurrentFilters()();
    return JSON.stringify(currentFilters) !== JSON.stringify(emptyFilters);
  });

  public controls: FormGroup<{ [x: string]: FormControl }> = this._fb.group({});

  constructor(
    @Inject(SIDEPANEL_DATA) private readonly data: { currentFilters: Filters; availableFilters: FilterTextBox[] },
    private readonly _sidePanelRef: SidePanelRef
  ) {}

  public ngOnInit(): void {
    this.buildFiltersForm();
    this.setFilterValues();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getDefinedFilters(): Filters {
    const parsedControls = this.controls as FormGroup<{ [x: string]: FormControl<string> }>;

    return {
      status: parsedControls.get('status')?.value || undefined,
      departureCode: parsedControls.get('departureCode')?.value || undefined,
      returnCode: parsedControls.get('returnCode')?.value || undefined,
      departureDate: parsedControls.get('departureDate')?.value || undefined,
      returnDate: parsedControls.get('returnDate')?.value || undefined,
      channelType: parsedControls.get('channelType')?.value || undefined,
      recordLocator: this.sanitizeRecordLocator(parsedControls.get('recordLocator')?.value),
      firstName: parsedControls.get('firstName')?.value || undefined,
      lastName: parsedControls.get('lastName')?.value || undefined
    };
  }

  public sendFilters(): void {
    if (this.controls.valid) {
      this._sidePanelService.sidePanelOutputEmit(this.getDefinedFilters());
      this._sidePanelRef.close();
    }
  }

  public deleteFilters(): void {
    this._bookingListFilterService.deleteFilters();
    this.controls.reset();
  }

  private formatDateToDDMMYYYY(date: string): string {
    const [day, month, year] = date.split('/');
    return `${day}/${month}/${year}`;
  }

  private buildFiltersForm(): void {
    this.controls = this._fb.group(
      Object.assign(
        {},
        ...this.data.availableFilters.map((filter) => {
          return {
            [filter.filterName]: new FormControl<string>('', filter.validator)
          };
        })
      )
    );
  }

  private setFilterValues(): void {
    const parsedControls = this.controls as FormGroup<{ [x: string]: FormControl<string> }>;

    Object.entries(this.data.currentFilters)
      .filter((filter) => !!filter[1])
      .forEach(([key, value]) => {
        let formValue = value as string;
        if ((key === 'status' && formValue.includes('QuickFilters')) || formValue === QuickFiltersValue.None) {
          return;
        }

        if (['departureDate', 'returnDate'].includes(key)) {
          formValue = this.formatDateToDDMMYYYY(formValue);
        }
        parsedControls.get(key)?.setValue(formValue);
      });
  }

  private sanitizeRecordLocator(value?: string): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed.toUpperCase() : undefined;
  }
}
